/**
 * Government code lists (Dukcapil) used by the KK member grid and the person record, plus
 * the Church-only occupation codes the annual statistics need. Seeded only from
 * research/DUKCAPIL-LISTS.md entries marked VERIFIED, verbatim; nothing here is from
 * memory. Every entry carries its `skema` because the same number means different things
 * in different official schemes (pendidikan 2 is "Belum Tamat SD" in SIAK and "PAUD/TK"
 * in Permendagri 6/2026). A later scheme change is therefore additive: new entries with a
 * new `skema`, old ones kept for old rows. These built-in lists are the only source the
 * forms read: the code lists are no longer edited in Administrasi (spec
 * 2026-09-30-administrasi 5), and the `kode_pendidikan` / `kode_pekerjaan` tables stay only
 * because migrations never drop a table.
 */
import type { HUBUNGAN } from '../schema/contract'
import pekerjaanJson from './pekerjaan.json' with { type: 'json' }
import pendidikanJson from './pendidikan.json' with { type: 'json' }
import shdkJson from './shdk.json' with { type: 'json' }
import statusKawinJson from './status-kawin.json' with { type: 'json' }

export interface EntriKode {
  readonly kode: string
  /** What the screen shows. */
  readonly label: string
  /** The official spelling when it differs from `label` (print artefacts, one typo). */
  readonly labelResmi?: string
  /** Which official code scheme the code belongs to. */
  readonly skema: string
  readonly urutan: number
  /** Not a government code; the UI marks these with a dagger. */
  readonly tambahanGereja?: true
  readonly catatan?: string
}

export interface SkemaKode {
  readonly id: string
  readonly nama: string
  readonly jumlahKode: number
  readonly sumber: string
  readonly keterangan: string
}

export interface DaftarKode {
  readonly skema: SkemaKode
  readonly entri: readonly EntriKode[]
}

export const PEKERJAAN: DaftarKode = pekerjaanJson as DaftarKode
export const PENDIDIKAN: DaftarKode = pendidikanJson as DaftarKode
export const SHDK: DaftarKode = shdkJson as DaftarKode
export const STATUS_KAWIN: DaftarKode = statusKawinJson as DaftarKode

export function cariKode(daftar: DaftarKode, kode: string): EntriKode | undefined {
  return daftar.entri.find((e) => e.kode === kode)
}

/** Display label for a code, or the code itself when unknown (old rows, edited lists). */
export function labelKode(daftar: DaftarKode, kode: string | null | undefined): string {
  if (kode === null || kode === undefined) return ''
  return cariKode(daftar, kode)?.label ?? kode
}

/** Rows for seeding `kode_pendidikan` and `kode_pekerjaan` (spec 8.8). */
export function barisKode(daftar: DaftarKode): Array<{
  kode: string
  label: string
  urutan: number
  tambahan_gereja: boolean
}> {
  return daftar.entri.map((e) => ({
    kode: e.kode,
    label: e.label,
    urutan: e.urutan,
    tambahan_gereja: e.tambahanGereja === true,
  }))
}

type Hubungan = (typeof HUBUNGAN)[number]

/**
 * The Church household role of a government SHDK code, for a card read or an import to fill
 * `hubungan`. The Church enum has no menantu, cucu, mertua or pembantu, so those become
 * famili_lain; the KK grid offers only the roles SATU stores (owner, 2026-10-04, A10). Code 1
 * is the head only for the first row.
 */
export const HUBUNGAN_DARI_SHDK: Readonly<Record<string, Hubungan>> = {
  '1': 'kepala',
  '2': 'suami',
  '3': 'istri',
  '4': 'anak',
  '5': 'famili_lain',
  '6': 'famili_lain',
  '7': 'orang_tua',
  '8': 'famili_lain',
  '9': 'famili_lain',
  '10': 'famili_lain',
  '11': 'famili_lain',
}
